import { useRef, useState, useEffect } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCrud } from "./useCrud";
import { Field, ImageField } from "./ImageField";
import { createTileMap, pctToLatLng, latLngToPct, pinIcon } from "../../lib/mapTiles";

const AdminMap = ({ items, onMove, onSelect, focusId }) => {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});
  const draggingIdRef = useRef(null);
  const cbRef = useRef({ onMove, onSelect });
  cbRef.current = { onMove, onSelect };

  useEffect(() => {
    if (mapRef.current || !divRef.current) return;
    const { map } = createTileMap(divRef.current, { zoomControl: true });
    const layer = L.layerGroup().addTo(map);
    layerRef.current = layer;
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    return () => { map.remove(); mapRef.current = null; markersRef.current = {}; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    const seen = new Set();
    items.forEach((b) => {
      seen.add(b.id);
      let m = markersRef.current[b.id];
      const latlng = pctToLatLng(map, b.x, b.y);
      if (!m) {
        m = L.marker(latlng, { icon: pinIcon(b.for_sale), draggable: true, autoPan: true });
        m.bindTooltip(b.name, { direction: "top", offset: [0, -26] });
        m.on("click", () => cbRef.current.onSelect(b.id));
        m.on("dragstart", () => { draggingIdRef.current = b.id; });
        m.on("dragend", () => { draggingIdRef.current = null; const p = latLngToPct(map, m.getLatLng()); cbRef.current.onMove(b.id, p.x, p.y); });
        m.addTo(layer);
        markersRef.current[b.id] = m;
      } else {
        // Don't yank a pin the user is mid-drag; only reposition if it actually moved.
        if (draggingIdRef.current !== b.id) {
          const cur = m.getLatLng();
          if (Math.abs(cur.lat - latlng.lat) > 1e-6 || Math.abs(cur.lng - latlng.lng) > 1e-6) m.setLatLng(latlng);
        }
        m.setIcon(pinIcon(b.for_sale));
        m.setTooltipContent(b.name);
      }
    });
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) { layer.removeLayer(markersRef.current[id]); delete markersRef.current[id]; }
    });
  }, [items]);

  // Fly to a searched business and flash its tooltip so it's easy to grab.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusId) return;
    const m = markersRef.current[focusId];
    if (!m) return;
    map.flyTo(m.getLatLng(), 8, { duration: 0.6 });
    m.openTooltip();
    const el = m.getElement();
    if (el) { el.classList.add("pin-flash"); setTimeout(() => el.classList.remove("pin-flash"), 2200); }
  }, [focusId]);

  return <div ref={divRef} data-testid="admin-business-map" className="w-full aspect-[2000/1483] bg-[#141210]" style={{ zIndex: 0 }} />;
};

export const BusinessesEditor = () => {
  const { items, setItem, patch, add, save, remove } = useCrud("businesses", { name: "New Business", type: "Shop", body: "", image: "", for_sale: false, price: "", x: 50, y: 50 });
  const [active, setActive] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [q, setQ] = useState("");

  const onMove = (id, x, y) => {
    setItem(id, { x, y });
    const it = items.find((i) => i.id === id);
    if (it) save({ ...it, x, y });
  };

  const jumpTo = (id) => {
    setActive(id);
    // re-trigger flyTo even if the same pin is picked twice
    setFocusId(null);
    setTimeout(() => setFocusId(id), 0);
    const el = document.querySelector(`[data-testid="business-edit-${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const matches = q.trim()
    ? items.filter((b) => `${b.name} ${b.town || ""} ${b.type || ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];

  return (
    <div className="space-y-6">
      <p className="text-[#8F877C] text-sm">Search a business to jump the map straight to its pin, then drag the pin onto its real building (auto-saves on release). Zoom in for precise placement.</p>

      <div className="relative max-w-md">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="business-map-search"
          placeholder="Search a business to jump to its pin…"
          className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal" />
        {matches.length > 0 && (
          <div className="absolute z-[500] mt-1 w-full max-h-64 overflow-auto border border-[#2A201A] bg-obsidian shadow-xl" data-testid="business-map-search-results">
            {matches.map((b) => (
              <button key={b.id} type="button" data-testid={`business-jump-${b.id}`}
                onClick={() => { jumpTo(b.id); setQ(""); }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-surface transition-colors">
                <span className="text-sm text-bone">{b.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#8F877C]">{b.town || b.type} {b.for_sale ? "· for sale" : ""}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative overflow-hidden border border-[#2A201A]">
        <AdminMap items={items} onMove={onMove} onSelect={setActive} focusId={focusId} />
      </div>

      <div className="flex justify-end">
        <button onClick={add} data-testid="business-add-btn" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add Business</button>
      </div>

      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} data-testid={`business-edit-${b.id}`} onMouseEnter={() => setActive(b.id)}
            className={`border p-4 space-y-3 ${active === b.id ? "border-teal/40 bg-surface" : "border-[#2A201A] bg-surface/40"}`}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" value={b.name} onChange={(v) => patch(b.id, "name", v)} />
              <Field label="Type / Category" value={b.type} onChange={(v) => patch(b.id, "type", v)} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Town" value={b.town} onChange={(v) => patch(b.id, "town", v)} />
              <Field label="Description / note" value={b.body} onChange={(v) => patch(b.id, "body", v)} />
            </div>
            <ImageField label="Photo (optional)" value={b.image} onChange={(v) => patch(b.id, "image", v)} />
            <div className="grid gap-3 sm:grid-cols-[auto_1fr] items-end">
              <button type="button" onClick={() => patch(b.id, "for_sale", !b.for_sale)} data-testid={`business-forsale-${b.id}`}
                className={`inline-flex items-center gap-2 font-feature text-xs uppercase tracking-wider border px-4 py-2.5 transition-colors ${b.for_sale ? "border-teal bg-teal text-obsidian" : "border-[#2A201A] text-[#8F877C] hover:border-teal/50"}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${b.for_sale ? "bg-obsidian" : "bg-[#5c554c]"}`} />
                {b.for_sale ? "For Sale (up for grabs)" : "Owned / occupied"}
              </button>
              <Field label="Listing price (shown when For Sale)" value={b.price} onChange={(v) => patch(b.id, "price", v)} placeholder="e.g. $5,000" />
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] text-[#8F877C]">x:{b.x} y:{b.y}</span>
              <button onClick={() => save(b)} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
              <button onClick={() => remove(b.id)} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
